import classNames from "classnames";
import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { useOutsideClick } from "../../hooks";
import { ChevronDownIcon, ChevronUpIcon, LanguageIcon } from "../Icons";
import { TSelectOption } from "../Select";
import "./language-selector.scss";

export type TLanguageSelector = {
    languageOptions: TSelectOption[];
    defaultLanguage: string;
    onLanguageChange: (language: string) => void;
    showCountryCodeOnly?: boolean;
};

export const LanguageSelector = ({
    languageOptions,
    defaultLanguage,
    onLanguageChange,
    showCountryCodeOnly = false,
}: TLanguageSelector) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState<string>();
    const languageSelectorRef = useRef<HTMLDivElement>(null);
    const dropdownId = useId();

    const setDefaultLanguage = () => {
        const firstLanguage = languageOptions[0]?.value as string;

        if (!defaultLanguage) {
            return firstLanguage;
        }

        return (
            (languageOptions.find(option => (option.value as string) === defaultLanguage)?.value as string) ||
            firstLanguage // Default to first language if default language is not found.
        );
    };

    useOutsideClick(isOpen, languageSelectorRef, () => setIsOpen(false));

    useLayoutEffect(() => {
        setSelectedLanguage(setDefaultLanguage());
        // eslint-disable-next-line
    }, [defaultLanguage]);

    const handleOptionClick = (option: TSelectOption) => {
        setSelectedLanguage(option.value as string);
        onLanguageChange(option.value as string);
        setIsOpen(false);
    };

    const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLLIElement>, option: TSelectOption) => {
        if (e.key === "Enter") {
            handleOptionClick(option);
        }
    };

    const iconClassName = "c-language-selector__icon";
    const optionsClassName = classNames("c-language-selector__options", {
        "c-language-selector__options--open": isOpen,
    });

    const getLabel = (option?: TSelectOption) => {
        if (!option) return undefined;
        return showCountryCodeOnly ? option.value : option.label;
    };

    // Get the longest label to set the width of button.
    const longestOptionLabel = getLabel(
        languageOptions.find(
            option =>
                (option.label?.length ?? 0) === Math.max(...languageOptions.map(option => option.label?.length ?? 0))
        )
    );

    const selectedLanguageLabel = getLabel(languageOptions.find(option => option.value === selectedLanguage));

    return (
        <div ref={languageSelectorRef} className="c-language-selector">
            <button
                type="button"
                className="c-language-selector__btn"
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                aria-controls={dropdownId}
                aria-haspopup="menu"
            >
                <LanguageIcon size={14} className={iconClassName} />
                <div data-longest={longestOptionLabel} className="c-language-selector__selected-language">
                    <span className="c-language-selector__selected-language-label">
                        {selectedLanguageLabel ?? selectedLanguage}
                    </span>
                </div>
                {isOpen ? (
                    <ChevronUpIcon size={14} className={iconClassName} />
                ) : (
                    <ChevronDownIcon size={14} className={iconClassName} />
                )}
            </button>
            <ul id={dropdownId} role="menu" className={optionsClassName} aria-hidden={!isOpen}>
                {languageOptions
                    .filter(option => option.value !== selectedLanguage)
                    .map((option: TSelectOption) => (
                        <li
                            key={option.value}
                            role="menuitem"
                            className="c-language-selector__option"
                            onClick={() => handleOptionClick(option)}
                            onKeyDown={e => handleOptionKeyDown(e, option)}
                            tabIndex={0}
                        >
                            {getLabel(option) || option.value}
                        </li>
                    ))}
            </ul>
        </div>
    );
};
